import { useId, type ReactNode } from "react";
import type { DashboardPeriodMetadata } from "@/features/dashboard/types";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import { DashboardPeriodControl } from "./DashboardPeriodControl";
import "./DashboardLedger.css";

export interface DashboardFrameProps {
  title: string;
  action?: ReactNode;
  period?: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  periodMetadata?: DashboardPeriodMetadata;
  periodNow?: Date;
  onPeriodChange?: (period: DashboardPeriodState) => void;
  updating?: boolean;
  children: ReactNode;
  className?: string;
}

export function DashboardFrame({
  title,
  action,
  period,
  pendingPeriod,
  periodMetadata,
  periodNow,
  onPeriodChange,
  updating = false,
  children,
  className,
}: DashboardFrameProps) {
  const titleId = `dashboard-ledger-title-${useId()}`;
  return (
    <section
      className={`dashboard-ledger dashboard-ledger__frame${className ? ` ${className}` : ""}`}
      aria-labelledby={titleId}
      aria-busy={updating ? "true" : "false"}
    >
      <header className="dashboard-ledger__header">
        <div className="dashboard-ledger__title-row" data-header-row="title">
          <h1 id={titleId} className="dashboard-ledger__title">
            {title}
          </h1>
        </div>
        <div
          className="dashboard-ledger__header-controls"
          data-has-action={action ? "true" : "false"}
        >
          {action ? (
            <div
              className="dashboard-ledger__header-action"
              data-mobile-half="action"
            >
              {action}
            </div>
          ) : null}
          <div
            className="dashboard-ledger__header-period"
            data-mobile-half="period"
          >
            <DashboardPeriodControl
              value={period}
              pendingValue={pendingPeriod}
              metadata={periodMetadata}
              now={periodNow}
              onChange={onPeriodChange}
            />
          </div>
        </div>
      </header>
      {updating ? (
        <div className="dashboard-ledger__updating-line" aria-hidden="true" />
      ) : null}
      <div className="dashboard-ledger__content">{children}</div>
    </section>
  );
}
